import { Injectable, inject } from "@angular/core";
import type { DashboardWidgetFragment, DashboardWidgetSetInput, DashboardWidgetViewFragment } from "@ankaadia/graphql";
import { GetDashboardWidgetGQL, GetVisibleDashboardWidgetsGQL, SetDashboardWidgetGQL } from "@ankaadia/graphql";
import type { Observable } from "rxjs";
import { map } from "rxjs";

/**
 * All widgets of an organization and their logo settings are one document, which is loaded and saved as a whole. The
 * `_etag` of the loaded document is part of the input, so the server rejects the save if somebody else saved in the
 * meantime.
 */
@Injectable({ providedIn: "root" })
export class DashboardWidgetService {
  private readonly getDashboardWidget = inject(GetDashboardWidgetGQL);
  private readonly setDashboardWidget = inject(SetDashboardWidgetGQL);
  private readonly getVisibleDashboardWidgets = inject(GetVisibleDashboardWidgetsGQL);

  /**
   * Always from the server: the `_etag` of a cached document would make the next save fail.
   */
  get(organizationId: string): Observable<DashboardWidgetFragment> {
    return this.getDashboardWidget
      .fetch({ input: { organizationId } }, { fetchPolicy: "network-only" })
      .pipe(map((x) => x.data.getDashboardWidget));
  }

  set(input: DashboardWidgetSetInput): Observable<DashboardWidgetFragment> {
    return this.setDashboardWidget
      .mutate(
        { input },
        {
          update: (cache) =>
            cache.modify({
              fields: {
                getVisibleDashboardWidgets: (_, helpers) => helpers.DELETE,
              },
            }),
        }
      )
      .pipe(map((x) => x.data.setDashboardWidget));
  }

  getVisible(): Observable<DashboardWidgetViewFragment[]> {
    return this.getVisibleDashboardWidgets.fetch().pipe(map((x) => x.data.getVisibleDashboardWidgets));
  }
}
